import { shallowRef, watch } from 'vue'
import { HubConnectionBuilder, HubConnection, HttpTransportType } from '@microsoft/signalr'
import { rootStore } from '@/store/index'
import { workerStore } from '@/store/worker'
import { chatsStore } from '@/store/chats'
import { headerStore } from '@/store/header'
import { ChatsApi } from '@/ api/ChatsApi'
import { useSoundAlert } from './useSoundAlert'
import { useNotification } from './useNotification'

import { i18n } from "@/i18n";

const t = (key: string, named?: Record<string, unknown>): string =>
	String(named ? i18n.global.t(key, named) : i18n.global.t(key));
interface MessageSentPayload {
    Chat: number
    NotifiedWorkers: number[]
    UnnotifiedWorkers: number[]
    SendTime: string
}

interface MessageReadPayload {
    MessageCrossId: number
    Writer: number
    Reader: number
    SendTime: string
}

interface JobNotificator {
    Worker: number
    AddedNotificationCount: number
    RemovedNotificationCount: number
    PlaySound: boolean
    SendTime: string
}

interface JobNotificatorGiver {
    List: JobNotificator[]
}

type JobEventKind = 'added' | 'changed' | 'removed'

const CHAT_HUB_URL = `${process.env.VUE_APP_API_OLD_URL}/chatHub`
const JOB_HUB_URL = `${process.env.VUE_APP_API_OLD_URL}/jobNotificationHub`

const formatSendTime = (isoString: string): string => {
    const date = new Date(isoString)

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`
}

let signalRInstance: ReturnType<typeof createSignalRService>

function createSignalRService() {
    const chatConnection = shallowRef<HubConnection | null>(null)
    const jobConnection = shallowRef<HubConnection | null>(null)
    const isConnected = shallowRef(false)

    const buildConnection = (url: string): HubConnection => {
        const store = rootStore()
        return new HubConnectionBuilder()
            .withUrl(url, {
                accessTokenFactory: () => store.globalJWT,
                skipNegotiation: true,
                transport: HttpTransportType.WebSockets,
            })
            .withAutomaticReconnect()
            .build()
    }

    const startChatHub = () => {
        const hub = buildConnection(CHAT_HUB_URL)

        hub.on('MessageSent', (data: string) => {
            try {
                const payload: MessageSentPayload = JSON.parse(data)
                handleMessageSent(payload)
            } catch {
                handleMessageSent(data as unknown as MessageSentPayload)
            }
        })

        hub.on('MessageRead', (data: string) => {
            try {
                const payload: MessageReadPayload = JSON.parse(data)
                handleMessageRead(payload)
            } catch {
                handleMessageRead(data as unknown as MessageReadPayload)
            }
        })

        hub.onclose(() => {
            isConnected.value = false
            console.warn('SignalR chatHub connection closed')
        })

        hub.onreconnected(() => {
            isConnected.value = true
            console.log('SignalR chatHub reconnected')
            // Во время обрыва могли потеряться эхо-события: досинхронизируем оба стора.
            resyncChatsAfterReconnect()
        })

        hub.start()
            .then(() => {
                isConnected.value = true
                console.log('SignalR connected to chatHub')
            })
            .catch(err => console.error('SignalR chatHub connection error:', err))

        chatConnection.value = hub
    }

    const startJobHub = () => {
        const hub = buildConnection(JOB_HUB_URL)

        hub.on('AddNewJob', (data: string) => handleJobEvent(data, 'added'))
        hub.on('JobChanged', (data: string) => handleJobEvent(data, 'changed'))
        hub.on('RemoveJob', (data: string) => handleJobEvent(data, 'removed'))

        hub.onclose(() => {
            console.warn('SignalR jobNotificationHub connection closed')
        })

        hub.onreconnected(() => {
            console.log('SignalR jobNotificationHub reconnected')
        })

        hub.start()
            .then(() => console.log('SignalR connected to jobNotificationHub'))
            .catch(err => console.error('SignalR jobNotificationHub connection error:', err))

        jobConnection.value = hub
    }

    const start = () => {
        const store = rootStore()
        if (!store.jwtToken) return

        startChatHub()
        startJobHub()
    }

    const stop = () => {
        if (chatConnection.value) {
            chatConnection.value.stop()
            chatConnection.value = null
        }
        if (jobConnection.value) {
            jobConnection.value.stop()
            jobConnection.value = null
        }
        isConnected.value = false
    }

    const handleMessageSent = async (data: MessageSentPayload) => {
        try {
            const chat = chatsStore()
            const header = headerStore()
            const worker = workerStore()
            const { playMessageAlert } = useSoundAlert()

            const currentWorkerId = worker.workerProfile?.Id
            if (!currentWorkerId) return

            // Диалоги обновляем ОДНИМ запросом и раздаём в оба стора:
            // это держит и список на Главной, и бейдж непрочитанных в шапке актуальными.
            try {
                const dialogs = await ChatsApi.refreshDialogInList(formatSendTime(data.SendTime))
                chat.applyDialogList(dialogs)
                header.applyDialogList(dialogs)
            } catch (e) {
                console.error('refresh dialogs (MessageSent) failed', e)
            }

            // Сообщения дотягиваем по каждому стору, где этот чат реально открыт.
            // Без guard: каждый GET честно ловит и своё сообщение (вернёт пусто), и чужое.
            let anyActive = false
            for (const s of [chat, header]) {
                if (s.chatsById[data.Chat]) {
                    const messages = s.messagesByChatId[data.Chat] || []
                    const lastMessageId = messages.length ? messages[messages.length - 1].Id : 0
                    const refreshed = await ChatsApi.refreshChatMessagesSecond(data.Chat, lastMessageId)
                    if (refreshed.Messages.length > 0) {
                        s.updateChatMessages(data.Chat, refreshed.Messages)
                    }
                }
                if (s.openedChat === data.Chat) anyActive = true
            }

            if (anyActive) return
            if (!data.NotifiedWorkers.includes(currentWorkerId)) return

            if (header.enableSound) {
                playMessageAlert()
            } else if (!document.hidden) {
                useNotification().info(t('messages_single'), t('new_message_received'))
            }
        } catch (e) {
            console.error('Error handling MessageSent:', e)
        }
    }

    const handleMessageRead = async (data: MessageReadPayload) => {
        try {
            const me = workerStore().workerProfile?.Id
            if (!me || data.Reader === me || data.Writer !== me) {
                return
            }

            for (const s of [chatsStore(), headerStore()]) {
                const found = s.applyMessageReadByOther(data.MessageCrossId)
                if (!found) {
                    await s.updateDialogInList(formatSendTime(data.SendTime))
                }
            }
        } catch (e) {
            console.error('Error handling MessageRead:', e)
        }
    }

    const parseJobPayload = (raw: unknown): JobNotificatorGiver | null => {
        try {
            return typeof raw === 'string'
                ? JSON.parse(raw) as JobNotificatorGiver
                : raw as JobNotificatorGiver
        } catch {
            return null
        }
    }

    const handleJobEvent = async (raw: unknown, kind: JobEventKind) => {
        try {
            const payload = parseJobPayload(raw)
            if (!payload?.List?.length) return

            const myId = workerStore().workerProfile?.Id
            if (!myId) return

            const myEntries = payload.List.filter(c => c.Worker === myId)
            if (myEntries.length === 0) return

            await headerStore().refreshAfterSocket()

            if (kind !== 'added') return

            const shouldAlert = myEntries.some(c => c.PlaySound)
            if (!shouldAlert || document.hidden) return

            if (headerStore().enableSound) {
                useSoundAlert().playNotificationAlert()
            } else {
                useNotification().info(t('notification_single'), t('new_job_registered'))
            }
        } catch (e) {
            console.error('Error handling job event:', e)
        }
    }

    const resyncChatsAfterReconnect = async () => {
        try {
            const chat = chatsStore()
            const header = headerStore()

            // Полная перезагрузка списка диалогов и раздача в оба стора.
            const dialogs = await ChatsApi.getAllDialogs()
            chat.applyDialogList(dialogs)
            header.applyDialogList(dialogs)

            // Догоняем сообщения открытых чатов каждого стора.
            for (const s of [chat, header]) {
                const openedChat = s.openedChat
                if (openedChat && s.chatsById[openedChat]) {
                    const messages = s.messagesByChatId[openedChat] || []
                    const lastMessageId = messages.length ? messages[messages.length - 1].Id : 0
                    const refreshed = await ChatsApi.refreshChatMessagesSecond(openedChat, lastMessageId)
                    if (refreshed.Messages.length > 0) {
                        s.updateChatMessages(openedChat, refreshed.Messages)
                    }
                }
            }
        } catch (e) {
            console.error('resync chats after reconnect failed', e)
        }
    }

    const init = () => {
        const store = rootStore()

        if (store.jwtToken) {
            start()
            return
        }

        const unwatch = watch(
            () => store.jwtToken,
            (newToken) => {
                if (newToken) {
                    start()
                    unwatch()
                }
            }
        )
    }

    return {
        isConnected,
        init,
        stop,
    }
}

export function useSignalR() {
    if (!signalRInstance) {
        signalRInstance = createSignalRService()
    }
    return signalRInstance
}
